/* eslint-disable max-lines */
import * as moment from "moment";
import * as React from "react";
import { debounce } from "lodash";
import {
    Container,
    Segment,
    Grid,
    List,
    Button,
    Loader,
    Divider,
    Header
} from "semantic-ui-react";
import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { createValidator } from "not-valid";
import { EmployeeRecord, ConsultationDetails } from "@teamprevent/common/model";
import { MultiCheckboxQuestion, MultiCheckbox } from "@teamprevent/common/questionnaires/components/questions/MultiCheckboxQuestion";
import { RadioButtonQuestion, RadioButton } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { QuestionnaireAnswer } from "@teamprevent/common/questionnaires/components/QuestionnaireAnswer";
import { AnsweredQuestion, AnsweredQuestionWithId, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { SavingIndicator } from "@teamprevent/common/components/SavingIndicator";

import { PrintDialog } from "@teamprevent/common/components/PrintDialog";

import {
    workClassificationOptions,
    fitnessOutcomeOptions,
    adjustmentOptions,
    adjustmentsRecommendedOptions,
    phasedReturnOptions,
    FitnessOutcome,
    SectionNames,
    ReferralReportDetails
} from "../models";
import { AdditionalInfo } from "../../questionnaires/questionnaireConfig/referral/questionIds";
import { TaskModel, TaskTypes } from "../../task/taskActions/models";

import * as questionIds from "../questionIds";
import { QuestionConfig } from "../../questionnaires/questionnaireConfig";
import { masterConfig } from "../masterConfig";
import { useHrAndLineManagers } from "../../reportsCommon/reportsCommon";

export const clinicianNameQuestion = { questionText: "Occupational Health Adviser", id: "CliniciansName" };
export const clinicianQualificationQuestion = { questionText: "", id: "CliniciansQualification" };
export const clinicianEmailQuestion = { questionText: "Clinician's email", id: "CliniciansEmail" };

export const QUESTIONNAIRE_KEY = "referralReport";

export interface ReferralReportOwnProps {
    submitQuestionnaire?: (report: ReferralReportModel) => Promise<boolean>;
    extraSections?: { title: string; id: string; content: React.ReactNode }[];
    extraQuestionContent?: { [questionId: string]: React.ReactNode };
}

export interface ReferralReportModel {
    questions: AnsweredQuestionWithId[];
    eTag: string;
    isDefaultCustomerType?: boolean;
    customerType?: string;
}

export interface ReferralReportStateProps {
    customerId: string;
    ohCaseId: string;
    taskId: string;
    task?: TaskModel;
    ohCase: { employeeRecordId: string };
    employeeRecord: EmployeeRecord;
    report: ReferralReportModel;
    readonly: boolean;
    consultationAndReferralDetails: ConsultationDetails;
    loading?: boolean;
    useToyotaTemplates: boolean;
}

export interface ReferralReportDispatchProps {
    loadEmployee: (employeeRecordId: string, customerId: string) => void;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }, questionnaireKey: string) => void;
    saveReport?: (ohCaseId: string, taskId: string, report: ReferralReportModel) => Promise<void>;
    loadQuestionnaire: (questionnaireKey: string, ohCaseId: string, taskId: string, customerId: string, omittedSections?: string[])
        => Promise<ReferralReportDetails>;
    clearQuestionnaire: (questionnaireKey: string) => void;
    submitInvalidQuestionnaire: (questionnaireKey: string) => void;
    onCompletion: () => void;
    loadConsultationDetails: (ohCaseId: string, customerId: string) => void;
    loadConsultationDetailsPreview: (ohCaseId: string, customerId: string) => void;
}

export type ReferralReportProps = ReferralReportStateProps & ReferralReportDispatchProps & ReferralReportOwnProps;

interface State {
    saving: boolean;
}

export class ReferralReport extends React.Component<ReferralReportProps, State> {
    public readonly summarySectionId = "Summary";
    public readonly reportDetailsSectionId = "ReportDetails";
    public readonly reportDateQuestion = { questionText: "Date", id: "Date" };
    public readonly workClassificationQuestion = { questionText: "Work Classification", id: "WorkClassificationText" };
    public readonly fitnessOutcomeQuestion = { questionText: "Fitness Outcome", id: "FitnessOutcomeText" };
    public readonly adjustmentsQuestion = { questionText: "Adjustments", id: "AdjustmentsText" };
    public readonly durationOfPhasedReturnQuestion = { questionText: "Recommended Length of Phased Return", id: "DurationOfPhasedReturnText" };
    public readonly adjustmentsRecommendedQuestion = { questionText: "Adjustments recommended", id: "AdjustmentsRecommendedText" };

    private saveDebounced: () => Promise<void>;

    constructor(props: ReferralReportProps) {
        super(props);
        this.loadQuestionnaire = this.loadQuestionnaire.bind(this);
        props.clearQuestionnaire(QUESTIONNAIRE_KEY);
        if (this.props.ohCase) {
            this.props.loadEmployee(this.props.ohCase.employeeRecordId, this.props.customerId);
        }
        if (this.props.taskId && this.props.ohCaseId) {
            this.props.loadConsultationDetails(this.props.ohCaseId, this.props.taskId);
        }
        this.saveDebounced = debounce(async () => {
            try {
                if (!this.props.readonly) {
                    await this.props.saveReport(this.props.ohCaseId, this.props.taskId, this.props.report);
                }
            } catch (ex) {
                if (ex.status === 400) {
                    this.loadQuestionnaire();
                } else {
                    throw ex;
                }
            }
            finally {
                this.setState({ saving: false });
            }
        }, 1500).bind(this);
        this.state = {
            saving: false
        };
    }

    public loadQuestionnaire() {
        let omittedSections: string[] = [];

        // AmendReportTask form shouldn't overwrite consultation details section from old form
        if (this.props.task?.type === TaskTypes.AmendReportTask) {
            omittedSections = [...omittedSections, SectionNames.ConsultationDetails];
        }

        this.props.loadQuestionnaire(QUESTIONNAIRE_KEY, this.props.ohCaseId, this.props.taskId, this.props.customerId, omittedSections);
    }

    private getQuestionConfig(questionId: string): QuestionConfig {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    }

    public componentDidUpdate?(prevProps: Readonly<ReferralReportProps>) {
        if (this.props.ohCase && this.props.ohCase !== prevProps.ohCase) {
            this.props.loadEmployee(this.props.ohCase.employeeRecordId, this.props.customerId);
        }
        if (this.props.taskId && this.props.taskId !== prevProps.taskId &&
            this.props.ohCaseId && this.props.ohCaseId !== prevProps.ohCaseId) {
            this.props.loadConsultationDetails(this.props.ohCaseId, this.props.taskId);
        }
        if (!this.props.readonly && this.props.saveReport && this.props.report.questions.some((q, i) => q !== prevProps.report.questions[i])) {
            this.setState({ saving: true }, this.saveDebounced);
        }
    }

    public render() {
        if (this.props.loading) {
            return <Loader active />;
        }

        const askAdjustmentsQuestion = ["ShortTerm", "LongTerm"].indexOf(this.getAnswerToQuestionWithId("Adjustments") as string) > -1;
        const askAdjustmentEndDateQuestion = this.getAnswerToQuestionWithId("Adjustments") === "ShortTerm";
        const workClassificationNotAtWork = this.getAnswerToQuestionWithId("WorkClassification") &&
            (this.getAnswerToQuestionWithId("WorkClassification") as string).startsWith("NotAtWork");
        const askPhasedReturnToWorkDuration = this.getAnswerToQuestionWithId("PhasedReturnToWorkRecommended") === "Yes";
        const addressString = this.props.employeeRecord && [
            this.props.employeeRecord.addressLine1,
            this.props.employeeRecord.addressLine2,
            this.props.employeeRecord.addressLine3,
            this.props.employeeRecord.county,
            this.props.employeeRecord.city,
            this.props.employeeRecord.county,
            this.props.employeeRecord.postCode
        ].filter(addressPart => !!addressPart).join("\n");
        const isPermanentlyUnfitForWork = this.getAnswerToQuestionWithId(questionIds.FitnessOutcome) === FitnessOutcome.PermanentlyUnfitForWork;

        const hrAndLineManagers = useHrAndLineManagers(this.props.consultationAndReferralDetails); // eslint-disable-line react-hooks/rules-of-hooks

        return (
            <Container className="referral-report">
                {this.props.readonly &&
                    <Grid container>
                        <Grid.Row className="report-title-row">
                            <Grid.Column>
                                <Header as="h1">Referral Outcome Report</Header>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                }
                <SavingIndicator display={this.state.saving} />
                <AccordionQuestionnaireContainer
                    loadQuestionnaire={this.loadQuestionnaire}
                    submitQuestionnaire={(questions) => this.props.submitQuestionnaire({ questions, eTag: this.props.report.eTag })}
                    readonly={this.props.readonly}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    extraQuestionContent={this.props.extraQuestionContent}
                    submissionConfirmationText={
                        `You are submitting this report for ${this.props.employeeRecord?.firstName} ${this.props.employeeRecord?.lastName}.
                        Are you happy to continue?`
                    }
                    questionnaireType={QuestionnaireTypes.Report}
                >
                    <AccordionQuestionnaireSection
                        title={this.props.readonly ? "Details" : "Referral Outcome Report"} id="ConsultationDetails" hideNextButton={true} active={true}
                    >
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "HrManager", questionText: "HR/Manager" })}
                            answer={hrAndLineManagers}
                            readonly
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "EmployeeSeen", questionText: "Employee Seen" })}
                            answer={this.props.employeeRecord
                                && `${this.props.employeeRecord.title} ${this.props.employeeRecord.firstName} ${this.props.employeeRecord.lastName}`}
                            readonly
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            hidden
                            questionConfig={({ id: "EmployeeAddress", questionText: "Address" })}
                            answer={addressString}
                            readonly
                        />
                        <OldDateQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "Dob", questionText: "Date of Birth" })}
                            answer={this.props.employeeRecord?.dateOfBirth.format("YYYY-MM-DD")}
                            readonly
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={this.getQuestionConfig(questionIds.EmployeeId)}
                            answer={this.props.consultationAndReferralDetails?.employeeId}
                            readonly
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "EmployerAndDivision", questionText: "Employer and Division" })}
                            answer={this.props.employeeRecord &&
                                [
                                    this.props.employeeRecord.customerName,
                                    this.props.employeeRecord.orgStructureOne?.name
                                ].filter(t => t).join(" - ")
                            }
                            readonly
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "JobTitle", questionText: "Job Title" })}
                            answer={this.props.employeeRecord?.jobTitle}
                            readonly
                        />
                        <OldDateQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "DateOfConsultation", questionText: "Date of Consultation" })}
                            answer={this.props.consultationAndReferralDetails?.consultationDate?.format("YYYY-MM-DD HH:mm")}
                            readonly
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "ConsultationType", questionText: "Consultation Type" })}
                            answer={this.props.consultationAndReferralDetails?.consultationType}
                            readonly
                        />
                        {!this.props.readonly &&
                            <Grid>
                                <Grid.Row>
                                    <Grid.Column width={16}>
                                        <Container textAlign="right">
                                            {this.props.ohCase &&
                                                <>
                                                    <a
                                                        href={`/customers/${this.props.customerId}/employeeRecords/${this.props.ohCase.employeeRecordId}`
                                                            + "/clinicalHistory"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <Button type="button" content="View Clinical History" />
                                                    </a>
                                                    <a
                                                        href={`/customers/${this.props.customerId}/employeeRecords/${this.props.ohCase.employeeRecordId}`
                                                            + `/ohCases/${this.props.ohCaseId}/${this.props.consultationAndReferralDetails.consultationTaskId}`
                                                            + "/referralClinicalNotes/"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <Button type="button" content="View Clinical Notes" />
                                                    </a>
                                                </>
                                            }
                                        </Container>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>}
                    </AccordionQuestionnaireSection>
                    <AccordionQuestionnaireSection title="Summary" id={this.summarySectionId} hideNextButton={true} active={true}>
                        <DropdownQuestion
                            hideDivider
                            inline
                            readonly={this.props.readonly}
                            questionConfig={({ id: "WorkClassification", questionText: this.workClassificationQuestion.questionText })}
                            options={workClassificationOptions.sort((a, b) => a.text.localeCompare(b.text)).map(o => ({ text: o.text, value: o.value }))}
                            mandatory
                            onAnswerChange={this.changeWorkClassification}
                            hidden={this.props.readonly}
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={this.workClassificationQuestion}
                            readonly={this.props.readonly}
                            hidden={!this.props.readonly}
                            mandatory
                        />
                        {workClassificationNotAtWork &&
                            <OldDateQuestion
                                hideDivider
                                inline
                                questionConfig={({ id: "FirstDayOfAbsence", questionText: "First Day of Absence" })}
                                readonly={this.props.readonly}
                                mandatory={workClassificationNotAtWork}
                                validators={[
                                    createValidator((value: moment.Moment) => this.isDateInPast(value), "This date must be in the past")
                                ]}
                            />
                        }
                        <DropdownQuestion
                            hideDivider
                            inline
                            readonly={this.props.readonly}
                            questionConfig={({ id: questionIds.FitnessOutcome, questionText: this.fitnessOutcomeQuestion.questionText })}
                            options={fitnessOutcomeOptions.sort((a, b) => a.text.localeCompare(b.text))}
                            mandatory
                            onAnswerChange={this.changeFitnessOutcome}
                            hidden={this.props.readonly}
                        />
                        {workClassificationNotAtWork &&
                            <OldDateQuestion
                                hideDivider
                                inline
                                questionConfig={({ id: "AdvisedTargetReturnToWorkDate", questionText: "Advised Target Return to Work Date" })}
                                readonly={this.props.readonly}
                                mandatory={workClassificationNotAtWork && !isPermanentlyUnfitForWork}
                                validators={[
                                    createValidator((value: moment.Moment) => !this.isDateInPast(value), "This date must not be in the past")
                                ]}
                            />
                        }
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={this.fitnessOutcomeQuestion}
                            readonly={this.props.readonly}
                            hidden={!this.props.readonly}
                            mandatory
                        />
                        <DropdownQuestion
                            hideDivider
                            inline
                            readonly={this.props.readonly}
                            questionConfig={({ id: "Adjustments", questionText: this.adjustmentsQuestion.questionText })}
                            options={adjustmentOptions}
                            mandatory
                            onAnswerChange={this.changeAdjustments}
                            hidden={this.props.readonly}
                        />
                        <TextQuestion
                            hideDivider
                            inline
                            questionConfig={this.adjustmentsQuestion}
                            readonly={this.props.readonly}
                            hidden={!this.props.readonly}
                            mandatory
                        />
                        {
                            askAdjustmentsQuestion &&
                            <MultiCheckboxQuestion
                                hideDivider
                                inline
                                questionConfig={({ id: "AdjustmentsRecommended", questionText: this.adjustmentsRecommendedQuestion.questionText })}
                                readonly={this.props.readonly}
                                mandatory={askAdjustmentsQuestion}
                                onAnswerChange={this.changeAdjustmentsRecommended}
                                options={adjustmentsRecommendedOptions}
                            >
                                {(Object.keys(adjustmentsRecommendedOptions) as (keyof typeof adjustmentsRecommendedOptions)[]).map(k =>
                                    <MultiCheckbox key={k} value={k} label={adjustmentsRecommendedOptions[k]} />)}
                            </MultiCheckboxQuestion>
                        }
                        {
                            askAdjustmentEndDateQuestion &&
                            <OldDateQuestion
                                hideDivider
                                mandatory={askAdjustmentEndDateQuestion}
                                inline
                                questionConfig={({ id: "AdjustmentsEndDate", questionText: "End Date of Temporary Adjustments" })}
                                validators={[
                                    createValidator((value: moment.Moment) => !this.isDateInPast(value), "Must not be in the past"),
                                    createValidator((value: moment.Moment) => this.validateAdjustmentsEndDate(value),
                                        "Cannot be more than 12 weeks in the future")
                                ]}
                                readonly={this.props.readonly}
                            />
                        }
                        {
                            workClassificationNotAtWork &&
                            <RadioButtonQuestion
                                hideDivider
                                inline
                                questionConfig={({ id: "PhasedReturnToWorkRecommended", questionText: "Phased Return to Work recommended?" })}
                                readonly={this.props.readonly}
                                mandatory
                            >
                                <RadioButton label="Yes" value="Yes" />
                                <RadioButton label="No" value="No" />
                            </RadioButtonQuestion>
                        }
                        {
                            askPhasedReturnToWorkDuration &&
                            <DropdownQuestion
                                hideDivider
                                inline
                                readonly={this.props.readonly}
                                questionConfig={({ id: "DurationOfPhasedReturn", questionText: "Recommended Length of Phased Return" })}
                                options={phasedReturnOptions}
                                mandatory={askPhasedReturnToWorkDuration}
                                onAnswerChange={this.changeDurationOfPhasedReturn}
                                hidden={this.props.readonly}
                            />
                        }
                        {
                            askPhasedReturnToWorkDuration &&
                            <TextQuestion
                                hideDivider
                                inline
                                questionConfig={this.durationOfPhasedReturnQuestion}
                                readonly={this.props.readonly}
                                hidden={!this.props.readonly}
                                mandatory
                            />
                        }
                        <TextQuestion
                            hideDivider
                            inline
                            readonly={this.props.readonly}
                            questionConfig={({ id: "OnwardReferral", questionText: "Onward Referral" })}
                        />
                        <RadioButtonQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "GpSpecialistReportRequested", questionText: "GP/Specialist Report Requested" })}
                            readonly={this.props.readonly}
                            mandatory
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <RadioButtonQuestion
                            hideDivider
                            inline
                            questionConfig={({ id: "EqualityActLikelyToApply", questionText: "Is the Equality Act Likely to Apply" })}
                            readonly={this.props.readonly}
                            mandatory
                        >
                            <RadioButton label="Yes" value="Yes" />
                            <RadioButton label="No" value="No" />
                        </RadioButtonQuestion>
                        <Divider hidden />
                        <TextQuestion
                            hideDivider
                            readonly={this.props.readonly}
                            questionConfig={this.getQuestionConfig(questionIds.JustificationForEqualityActOpinion)}
                            multiline
                            mandatory
                        />
                    </AccordionQuestionnaireSection>
                    <AccordionQuestionnaireSection
                        id="BackGroundReasonsForReferral"
                        title="Background and Reason for Referral"
                        hideNextButton={true}
                        active={true}
                    >
                        <TextQuestion
                            hideDivider
                            readonly={this.props.readonly}
                            questionConfig={({ id: "BackGroundReasonsForReferral", questionText: "" })}
                            multiline
                        />
                    </AccordionQuestionnaireSection>
                    <AccordionQuestionnaireSection id="AssessmentAndOpinions" title="Assessment and Opinions" hideNextButton={true} active={true}>
                        <TextQuestion
                            hideDivider
                            readonly={this.props.readonly}
                            questionConfig={({ id: "AssessmentAndOpinions", questionText: "" })}
                            multiline
                        />
                    </AccordionQuestionnaireSection>
                    <AccordionQuestionnaireSection id="RecommendationsAndSummary" title="Information/Answers" hideNextButton={true} active={true}>
                        {this.props.consultationAndReferralDetails &&
                            this.props.consultationAndReferralDetails.informationRequiredSection &&
                            this.props.consultationAndReferralDetails.informationRequiredSection.some(q => !!q.answer) &&
                            <Grid.Row>
                                <List className={"triage-questionnaire-answers information-required"}>
                                    {this.props.consultationAndReferralDetails.informationRequiredSection.filter(q => q.answer).map(question => (
                                        <QuestionnaireAnswer
                                            // Display question text (which will include custom question text)
                                            // for checkbox questions, and answer text for text question
                                            question={{ ...question, answer: question.id === AdditionalInfo ? question.answer : question.question }}
                                            key={question.id}
                                        />))}
                                </List>
                            </Grid.Row>}
                        <TextQuestion
                            hideDivider
                            readonly={this.props.readonly}
                            questionConfig={({ id: "RecommendationsAndSummary", questionText: "" })}
                            multiline
                        />
                    </AccordionQuestionnaireSection>
                    {
                        this.props.readonly &&
                        <AccordionQuestionnaireSection title="Report details" id={this.reportDetailsSectionId} hideNextButton={true} active={true}>
                            <OldDateQuestion hideDivider inline readonly={true} questionConfig={this.reportDateQuestion} />
                            <Grid container className="inline-question-answer-grid">
                                <Grid.Row className="question-answer-row">
                                    <Grid.Column width="3" className="primary-column">Occupational Health Adviser</Grid.Column>
                                    <Grid.Column width="5" floated="left">
                                        {this.props.consultationAndReferralDetails?.occupationalHealthAdviserName}
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row className="question-answer-row">
                                    <Grid.Column width="3" className="primary-column"></Grid.Column>
                                    <Grid.Column width="5" floated="left" >
                                        {this.props.consultationAndReferralDetails?.occupationalHealthAdviserQualification}
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </AccordionQuestionnaireSection>
                    }
                    {
                        this.props.extraSections && this.props.extraSections.map((s, i) =>
                            (<AccordionQuestionnaireSection key={i} title={s.title} id={s.id} hideNextButton={true} active={true} neverHide={true}>
                                {s.content}
                            </AccordionQuestionnaireSection>))
                    }
                </AccordionQuestionnaireContainer>
                {this.props.readonly &&
                    <>
                        <Segment basic>
                            Please note that any advice given and suggested adjustments contained within this report are purely recommendations and the opinion
                            of the clinician. It is ultimately a management decision as to whether or not these can be accommodated.
                        </Segment>
                        <Divider />
                        <Container>
                            <PrintDialog />
                        </Container>
                    </>
                }
            </Container>
        );
    }

    private changeWorkClassification = (workClassification: string) => {
        const newWorkClassification = workClassificationOptions.find(o => o.value === workClassification);
        this.props.updateAnswer({
            sectionId: this.summarySectionId, id: this.workClassificationQuestion.id,
            question: { question: this.workClassificationQuestion.questionText, answer: newWorkClassification?.text, isValid: true }
        }, QUESTIONNAIRE_KEY);
    };

    private changeFitnessOutcome = (fitnessOutcome: string) => {
        const newFitnessOutcome = fitnessOutcomeOptions.find(o => o.value === fitnessOutcome);
        this.props.updateAnswer({
            sectionId: this.summarySectionId, id: this.fitnessOutcomeQuestion.id,
            question: { question: this.fitnessOutcomeQuestion.questionText, answer: newFitnessOutcome?.text, isValid: true }
        }, QUESTIONNAIRE_KEY);
    };

    private changeAdjustments = (adjustments: string) => {
        const newAdjustmentsValue = adjustmentOptions.find(o => o.value === adjustments);
        this.props.updateAnswer({
            sectionId: this.summarySectionId, id: this.adjustmentsQuestion.id,
            question: { question: this.adjustmentsQuestion.questionText, answer: newAdjustmentsValue?.text, isValid: true }
        }, QUESTIONNAIRE_KEY);
    };

    private changeDurationOfPhasedReturn = (phasedReturn: string) => {
        const newPhasedReturn = phasedReturnOptions.find(o => o.value === phasedReturn);
        this.props.updateAnswer({
            sectionId: this.summarySectionId, id: this.durationOfPhasedReturnQuestion.id,
            question: { question: this.durationOfPhasedReturnQuestion.questionText, answer: newPhasedReturn?.text, isValid: true }
        }, QUESTIONNAIRE_KEY);
    };

    private changeAdjustmentsRecommended = (answers: (keyof typeof adjustmentsRecommendedOptions)[]) =>
        this.props.updateAnswer({
            sectionId: this.summarySectionId,
            id: this.adjustmentsRecommendedQuestion.id,
            question: {
                sectionId: this.summarySectionId,
                id: this.adjustmentsRecommendedQuestion.id,
                question: this.adjustmentsRecommendedQuestion.questionText,
                isValid: true,
                answer: answers.map(a => adjustmentsRecommendedOptions[a])
            }
        }, QUESTIONNAIRE_KEY);

    private validateAdjustmentsEndDate(value: moment.Moment) {
        return value && value.isBetween(moment().startOf("day"), moment().add(12, "weeks"));
    }

    private isDateInPast(value: moment.Moment) {
        return value && value.isBefore(moment().startOf("day"));
    }

    private getAnswerToQuestionWithId(id: string) {
        return this.props.report.questions.find(question => question.id === id) && this.props.report.questions.find(question => question.id === id).answer;
    }
}
